import { useState } from 'react'
import { ChevronDown, ChevronRight, Download } from 'lucide-react'

import { Button } from '@/shared/ui/button'
import { t } from '@/shared/lib/i18n'
import type { WebAdminInstrumentTypeAttachment } from '@/shared/types/web'

type InstrumentTypeDocumentsDisclosureProps = {
  documents: WebAdminInstrumentTypeAttachment[]
}

const fileExtensionBadge = (attachmentName: string) =>
  attachmentName.split('.').pop()?.toUpperCase() ?? '—'

export const InstrumentTypeDocumentsDisclosure = ({
  documents,
}: InstrumentTypeDocumentsDisclosureProps) => {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-lg border p-3">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 text-sm font-medium"
        disabled={documents.length === 0}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('instruments.show.documents.toggle', { count: String(documents.length) })}
      </button>

      {open && documents.length > 0 && (
        <ul className="mt-3 divide-y rounded-md border">
          {documents.map((document) => (
            <li key={document.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                {fileExtensionBadge(document.attachmentName ?? '')}
              </span>

              <div className="min-w-0 flex-1">
                <a
                  href={document.downloadHref}
                  className="block truncate text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  {document.attachmentName}
                </a>
                {document.attachmentDescription && (
                  <p className="truncate text-xs text-muted-foreground">
                    {document.attachmentDescription}
                  </p>
                )}
              </div>

              <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                <a href={document.downloadHref} download>
                  <Download className="h-3.5 w-3.5" />
                  <span className="sr-only">{t('instruments.show.documents.downloadSr')}</span>
                </a>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default InstrumentTypeDocumentsDisclosure
